/**
 * Collection of utilities useful for broad offline usage depending on workspace or per app level needs
 */
/* eslint-disable */
import { HttpRequest } from '@angular/common/http';
import { StorageKeys } from './storage';
import { decodeJwt } from './strings';

/**
 * Identity the offline http cache is currently scoped to (#33947).
 *
 * The key used to be method + url ONLY, so every user of an install shared one
 * cache namespace and logout purged none of it: the next person to sign in read
 * the previous person's cached job list, crew rosters (incl. Last4SSN) and
 * userProfile straight back out of it whenever the app was offline. Verified on
 * a device that had held two Auth0 identities.
 *
 * Environment needs no component of its own: the API host is part of
 * `urlWithParams`, so stg and prod already key apart.
 *
 * Empty means "nobody has signed in yet" and resolves to `anon`, which keeps
 * pre-login responses from being read back as a signed-in user's.
 */
let httpCacheIdentityScope = '';

const HTTP_CACHE_SCOPE_SEPARATOR = '|';

type HttpCacheIdentityScopeChangingListener = (
  from: string,
  to: string
) => void;
const scopeChangingListeners =
  new Set<HttpCacheIdentityScopeChangingListener>();

/**
 * Run `listener` just BEFORE the scope moves to a different identity (a user
 * switch, a sign-out or a lost session). It is called while the OLD scope is
 * still active, so a store holding one user's in-memory state (the Foreman
 * selected job) can flush it under that user's key and then drop it, instead
 * of a later save writing it under the next user's key. Not called when the
 * scope is set to the value it already holds (e.g. a token refresh).
 * Returns an unsubscribe function.
 */
export function onHttpCacheIdentityScopeChanging(
  listener: HttpCacheIdentityScopeChangingListener
): () => void {
  scopeChangingListeners.add(listener);
  return () => scopeChangingListeners.delete(listener);
}

/**
 * Point the cache at a user. Call on sign-in with a stable per-user id
 * (the Auth0 user id), and with null on sign-out.
 *
 * `fillOnly` sets it only if nobody holds it yet. The profile path uses it, so
 * a token-derived scope (see `setHttpCacheIdentityScopeFromToken`) is never
 * overwritten by the profile's copy of the same id: if the two ever differed,
 * the cache would flip between two namespaces mid-session.
 */
export function setHttpCacheIdentityScope(
  scope: string | null | undefined,
  fillOnly = false
) {
  if (fillOnly && httpCacheIdentityScope) {
    return;
  }
  // An Auth0 user id is `auth0|<id>` — it contains the separator. Left raw it
  // would make the first `|` in the key ambiguous and every strip would cut in
  // the wrong place, so the separator is squeezed out of the scope itself.
  const next =
    scope != null
      ? String(scope).trim().split(HTTP_CACHE_SCOPE_SEPARATOR).join('_')
      : '';
  if (next === httpCacheIdentityScope) {
    return;
  }
  for (const listener of [...scopeChangingListeners]) {
    try {
      listener(httpCacheIdentityScope, next);
    } catch {
      // A failing listener must never block a sign-out from releasing the scope.
    }
  }
  httpCacheIdentityScope = next;
}

export function getHttpCacheIdentityScope() {
  return httpCacheIdentityScope || 'anon';
}

/**
 * True once a user owns the cache. Stores that hold one user's data (the
 * Foreman job list: crew rosters, Last4SSN, unsubmitted hours) must neither
 * read nor write while this is false: `anon` is shared by whoever is between
 * sessions, so anything written there after a logout would be read back by
 * the next person to sign in.
 */
export function isHttpCacheIdentityScoped() {
  return !!httpCacheIdentityScope;
}

/**
 * Point the cache at the user an access token belongs to, from its `sub`.
 *
 * The primary source on mobile, called whenever credentials are saved,
 * refreshed or restored from the keychain. That happens before any guarded
 * page loads, so the scope is right before the first cached read, offline
 * included, and a new sign-in replaces whatever scope a lost session left
 * behind. A token without a readable `sub` leaves the scope untouched.
 */
export function setHttpCacheIdentityScopeFromToken(
  accessToken: string | null | undefined
) {
  let sub: unknown;
  try {
    sub = accessToken ? decodeJwt(accessToken)?.sub : undefined;
  } catch {
    sub = undefined;
  }
  if (typeof sub === 'string' && sub.trim()) {
    setHttpCacheIdentityScope(sub);
  }
}

/**
 * Drop the identity prefix from a cache key, for comparing two keys by the
 * request they describe rather than by who cached it.
 */
export function stripHttpCacheIdentityScope(cacheKey: string) {
  if (!cacheKey) {
    return '';
  }
  // A legacy key written before scoping existed starts with the method, and a
  // url may itself contain a `|`. Cutting at the first separator regardless
  // would mangle those, so only strip what is genuinely a scope prefix.
  if (/^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)-/.test(cacheKey)) {
    return cacheKey;
  }
  const at = cacheKey.indexOf(HTTP_CACHE_SCOPE_SEPARATOR);
  return at === -1 ? cacheKey : cacheKey.slice(at + 1);
}

/**
 * Generate a unique key based on the signed-in user + http request method + urlWithParams
 * @param req HttpRequest
 * @returns string formatted for uniqueness
 */
export function httpCacheRequestKey(req: Partial<HttpRequest<any>>) {
  return `${getHttpCacheIdentityScope()}${HTTP_CACHE_SCOPE_SEPARATOR}${
    req.method
  }-${req.urlWithParams}`;
}

/**
 * In-Memory cache of http cacheKey's with their timestemps on when they were last stored.
 * These are integrated into the http-ups.interceptor to handle automatically.
 * The uilities here can be used to look at timestamps against any api cacheKey you'd like anywhere for various purposes.
 */
const httpCacheResponseTimestamps: { [key: string]: number } = {};
export function getHttpCacheResponseTimestamps(cacheKey: string): number {
  return httpCacheResponseTimestamps[cacheKey];
}
export function setHttpCacheResponseTimestamps(
  cacheKey: string,
  timestamp: number
) {
  httpCacheResponseTimestamps[cacheKey] = timestamp;
}

/**
 * Strips the domain/protocol from url to provide just the endpoint
 * @param url of request
 * @returns string of the endpoint
 */
export function httpEndpointFromUrl(url: string) {
  return url ? `/${url.split('/').splice(3).join('/')}` : '';
}

export type offlineStorageColumnType = 'dynamicDropdowns' | 'dynamicContainers';

/**
 * Cached offline Dynamic typeahead/dropdown api requests
 * Keys match those of the response to the consolidated prefetch call
 * https://dynamic-content-api.azurewebsites.net/api/Form/dropdowns
 */
export const offlineRequestDropdowns = {
  Companies: '/api/hqco/company',
  Clients: '/api/parentCustomers',
  Employees: '/api/spartaemployee/employee/dropdown',
  Facilities: '/api/parentFacilities',
  Jobs: '/api/jobs/dropdown',
  LessonLearnedCategories: '/api/Form/lessonLearnedCategories',
  NearMissCategories: '/api/Form/nearMissCategories',
};

/**
 * Define which endpoints use custom cache setups.
 * Adding an endpoint here will exclude it from default http-ups.interceptor cache behavior.
 */
let customCachedEndpoints: string[] = [];
export function getCustomCachedEndpoints() {
  return customCachedEndpoints;
}
export function setCustomCachedEndpoints(endpoints: string[]) {
  customCachedEndpoints = [...(customCachedEndpoints || []), ...endpoints];
}

/**
 * Cached offline Dynamic containers
 * Key is what the data is stored with in any platform storage database
 */
export const offlineRequestDynamicContainers = {
  data: '/api/DynamicContainer/all',
};

/**
 * Offline response handling can by derived from other stored requests (for example, /api/DynamicContainer/all)
 * This defines all potential api request formats which can be handled while offline when data is derived from a different stored request
 * @param cacheKey the offline cacheKey determined in the http-ups.interceptor
 * @returns the offlineStorageColumnType of where to get the data and the processing function to handle the result
 */
export function getOfflineRequestHandling(cacheKey: string): {
  cacheKey: string;
  prefetchCacheKey: offlineStorageColumnType;
  handleResponseBody: (res: { data: unknown }) => unknown;
} {
  if (offlineRequestChecks.isDynamicContainerAll(cacheKey)) {
    return {
      cacheKey: StorageKeys.OFFLINE_DATA_DYNAMIC_CONTAINERS,
      prefetchCacheKey: 'dynamicContainers',
      handleResponseBody: (res) => {
        if (res?.data) {
          return res.data;
        }
        return [];
      },
    };
  } else if (offlineRequestChecks.isDynamicContainer(cacheKey)) {
    // Opening a dynamic container will fetch this endpoint with this cacheKey:
    // * GET-https://qa-dynamic-content-api.azurewebsites.net/api/DynamicContainer/1b46244e-67f6-4cbb-9871-fa5f15349bb1/container/15
    // while offline, we can get it's data from the stored data off the /api/DynamicContainer/all endpoint
    return {
      cacheKey: StorageKeys.OFFLINE_DATA_DYNAMIC_CONTAINERS,
      prefetchCacheKey: 'dynamicContainers',
      handleResponseBody: (res) => {
        const parts = cacheKey
          .split('/api/DynamicContainer/')[1]
          .split('/container/');
        const containerId = parts[0];
        const version = +parts[1];
        if (res?.data) {
          return (<Array<unknown>>res.data).find(
            (c: {
              originalVersionContainerId: string;
              versionNumber: number;
            }) => {
              return (
                c.originalVersionContainerId === containerId &&
                c.versionNumber === version
              );
            }
          );
        }
        return null;
      },
    };
  }
  return null;
}

/**
 * Various api requests which are custom handled offline
 */
export const offlineRequestChecks = {
  isDynamicContainerAll(url: string) {
    return url.includes(offlineRequestDynamicContainers.data);
  },
  isDynamicContainer(url: string) {
    // TODO: use RegEx here instead when time permits
    return (
      url.indexOf('dynamic-content-api') > -1 &&
      url.indexOf('/api/DynamicContainer/') > -1 &&
      url.indexOf('/container/') > -1
    );
  },
};

/**
 * All prefetched http requests considered for offline usage
 * These can be direct prefetched requests or part of consolidated prefetch calls
 * Stores an array of api endpoints, e.g. ['/api/hqco/company', '/api/parentCustomers']
 */
export const httpPrefetchedRequestsForOffline: Array<string> = [
  '/api/Form/dropdowns',
  ...Object.keys(offlineRequestDropdowns).map(
    (r) => offlineRequestDropdowns[r]
  ),
  ...Object.keys(offlineRequestDynamicContainers).map(
    (r) => offlineRequestDynamicContainers[r]
  ),
];
